/* ==========================================================================
   STILL Meridian — app.js
   Vanilla custom elements. No dependencies.
   ========================================================================== */

(() => {
  "use strict";

  /* ------------------------------------------------------------------------
     Utilities
     ------------------------------------------------------------------------ */
  const REDUCED_MOTION = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const formatMoney = (cents) => {
    const format = (window.stillSettings && window.stillSettings.moneyFormat) || "${{amount}}";
    const value = (cents / 100).toFixed(2).replace(/\.00$/, "");
    return format.replace(/<[^>]*>/g, "").replace(/\{\{\s*amount[^}]*\}\}/, value).trim();
  };

  const announce = (message) => {
    const region = document.getElementById("A11yStatus");
    if (region) {
      region.textContent = "";
      requestAnimationFrame(() => (region.textContent = message));
    }
  };

  const trapFocus = (container, event) => {
    const focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const fetchConfig = (type = "json") => ({
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  });

  /* ------------------------------------------------------------------------
     Scroll reveal — with an IO health check.
     Some embedded webviews ship a native IntersectionObserver that never
     fires. If the sentinel below doesn't call back quickly, we abandon the
     reveal choreography and show everything. Content > motion.
     ------------------------------------------------------------------------ */
  let ioBroken = false;

  const forceRevealAll = () => {
    document.querySelectorAll("[data-reveal]").forEach((el) => el.classList.add("is-revealed"));
  };

  const revealObserver = REDUCED_MOTION
    ? null
    : new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-revealed");
              revealObserver.unobserve(entry.target);
            }
          });
        },
        { rootMargin: "0px 0px -8% 0px", threshold: 0.05 }
      );

  const observeReveals = (root = document) => {
    if (!revealObserver || ioBroken) {
      (root === document ? document : root)
        .querySelectorAll("[data-reveal]")
        .forEach((el) => el.classList.add("is-revealed"));
      return;
    }
    root.querySelectorAll("[data-reveal]:not(.is-revealed)").forEach((el) => revealObserver.observe(el));
  };

  const ioHealthCheck = () => {
    if (REDUCED_MOTION) return;
    let fired = false;
    try {
      const probe = new IntersectionObserver(() => {
        fired = true;
        probe.disconnect();
      });
      probe.observe(document.documentElement);
      setTimeout(() => {
        if (!fired) {
          ioBroken = true;
          forceRevealAll();
        }
      }, 900);
    } catch (e) {
      ioBroken = true;
      forceRevealAll();
    }
  };

  document.addEventListener("DOMContentLoaded", () => {
    observeReveals();
    ioHealthCheck();
  });
  document.addEventListener("shopify:section:load", (e) => observeReveals(e.target));

  /* ------------------------------------------------------------------------
     Sticky header — glass on scroll
     ------------------------------------------------------------------------ */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.header = this.querySelector(".site-header") || this;
      this.update = this.update.bind(this);
      window.addEventListener("scroll", this.update, { passive: true });
      this.update();
    }

    update() {
      this.header.classList.toggle("is-solid", window.scrollY > 12);
    }
  }
  customElements.define("sticky-header", StickyHeader);

  /* ------------------------------------------------------------------------
     Drawer base (menu + cart share behavior)
     ------------------------------------------------------------------------ */
  class BaseDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector("[data-drawer-panel]");
      this.addEventListener("click", (e) => {
        if (e.target.matches("[data-drawer-close], [data-drawer-close] *") || e.target.matches("[data-drawer-scrim]")) {
          this.close();
        }
      });
      this.addEventListener("keydown", (e) => {
        if (e.key === "Escape") this.close();
        if (e.key === "Tab" && this.classList.contains("is-open")) trapFocus(this, e);
      });
    }

    open(invoker) {
      this.invoker = invoker || document.activeElement;
      this.classList.add("is-open");
      this.removeAttribute("inert");
      document.body.classList.add("is-locked");
      const target = this.querySelector("[data-autofocus]") || this.querySelector("button, a");
      requestAnimationFrame(() => target && target.focus({ preventScroll: true }));
    }

    close() {
      if (!this.classList.contains("is-open")) return;
      this.classList.remove("is-open");
      this.setAttribute("inert", "");
      document.body.classList.remove("is-locked");
      if (this.invoker && this.invoker.focus) this.invoker.focus({ preventScroll: true });
    }
  }

  class MenuDrawer extends BaseDrawer {}
  customElements.define("menu-drawer", MenuDrawer);

  document.addEventListener("click", (e) => {
    const opener = e.target.closest("[data-drawer-open]");
    if (!opener) return;
    const drawer = document.getElementById(opener.getAttribute("data-drawer-open"));
    if (drawer && drawer.open) {
      e.preventDefault();
      drawer.open(opener);
    }
  });

  /* ------------------------------------------------------------------------
     Cart drawer — section rendering
     ------------------------------------------------------------------------ */
  class CartDrawer extends BaseDrawer {
    connectedCallback() {
      super.connectedCallback();
      this.addEventListener("change", (e) => {
        const input = e.target.closest("[data-line-qty]");
        if (input) this.updateLine(input.dataset.lineKey, parseInt(input.value, 10));
      });
      this.addEventListener("click", (e) => {
        const btn = e.target.closest("[data-line-remove]");
        if (btn) {
          e.preventDefault();
          this.updateLine(btn.dataset.lineKey, 0);
        }
      });
    }

    async updateLine(key, quantity) {
      const line = this.querySelector(`[data-line="${key}"]`);
      if (line) line.classList.add("is-updating");
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || "/"}cart/change.js`, {
          ...fetchConfig(),
          body: JSON.stringify({
            id: key,
            quantity,
            sections: "cart-drawer,cart-icon-bubble",
            sections_url: window.location.pathname,
          }),
        });
        const state = await response.json();
        if (state.errors) throw new Error(state.errors);
        this.renderState(state);
      } catch (err) {
        if (line) {
          line.classList.remove("is-updating");
          let msg = line.querySelector(".cart-error");
          if (!msg) {
            msg = document.createElement("p");
            msg.className = "cart-error";
            line.appendChild(msg);
          }
          msg.textContent = window.stillStrings.cartError;
        }
      }
    }

    renderState(state) {
      if (!state.sections) return;
      if (state.sections["cart-drawer"]) {
        const html = new DOMParser().parseFromString(state.sections["cart-drawer"], "text/html");
        const fresh = html.querySelector("#CartDrawerContent");
        const current = this.querySelector("#CartDrawerContent");
        if (fresh && current) current.replaceWith(fresh);
      }
      updateCartCount(state.sections["cart-icon-bubble"]);
      const count = state.item_count != null ? state.item_count : null;
      if (count !== null) announce(`${count} ${count === 1 ? "item" : "items"} in the bag`);
    }
  }
  customElements.define("cart-drawer", CartDrawer);

  const updateCartCount = (sectionHtml) => {
    if (!sectionHtml) return;
    const html = new DOMParser().parseFromString(sectionHtml, "text/html");
    const fresh = html.querySelector("[data-cart-count-wrap]");
    document.querySelectorAll("[data-cart-count-wrap]").forEach((el) => {
      if (fresh) el.innerHTML = fresh.innerHTML;
      const badge = el.querySelector(".cart-count");
      if (badge) {
        badge.classList.remove("is-popping");
        void badge.offsetWidth;
        badge.classList.add("is-popping");
      }
    });
  };

  /* ------------------------------------------------------------------------
     Product form — AJAX add to cart
     ------------------------------------------------------------------------ */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector("form");
      if (!this.form) return;
      this.form.addEventListener("submit", this.onSubmit.bind(this));
    }

    async onSubmit(e) {
      e.preventDefault();
      const submitButtons = [
        ...this.form.querySelectorAll('[type="submit"]'),
        ...document.querySelectorAll(`[type="submit"][form="${this.form.id}"]`),
      ];
      submitButtons.forEach((b) => b.classList.add("btn--busy"));

      const formData = new FormData(this.form);
      formData.append("sections", "cart-drawer,cart-icon-bubble");
      formData.append("sections_url", window.location.pathname);

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || "/"}cart/add.js`, {
          method: "POST",
          headers: { Accept: "application/javascript" },
          body: formData,
        });
        const result = await response.json();
        if (result.status) throw new Error(result.description || window.stillStrings.cartError);

        const drawer = document.getElementById("CartDrawer");
        if (drawer) {
          drawer.renderState({ sections: result.sections, item_count: null });
          drawer.open(submitButtons[0]);
        }
        announce(window.stillStrings.added);
      } catch (err) {
        let msg = this.querySelector(".cart-error");
        if (!msg) {
          msg = document.createElement("p");
          msg.className = "cart-error";
          this.form.appendChild(msg);
        }
        msg.textContent = err.message;
      } finally {
        submitButtons.forEach((b) => b.classList.remove("btn--busy"));
      }
    }
  }
  customElements.define("product-form", ProductForm);

  /* ------------------------------------------------------------------------
     Quantity stepper
     ------------------------------------------------------------------------ */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector("input");
      this.addEventListener("click", (e) => {
        const btn = e.target.closest("button");
        if (!btn) return;
        e.preventDefault();
        const delta = btn.dataset.qty === "up" ? 1 : -1;
        const min = parseInt(this.input.min || "1", 10);
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        const next = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || min) + delta));
        if (next !== parseInt(this.input.value, 10)) {
          this.input.value = next;
          this.input.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    }
  }
  customElements.define("quantity-input", QuantityInput);

  /* ------------------------------------------------------------------------
     Variant picker
     ------------------------------------------------------------------------ */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector("[data-variants-json]");
      if (!dataEl) return;
      this.variants = JSON.parse(dataEl.textContent);
      this.sectionId = this.dataset.sectionId;
      this.addEventListener("change", this.onChange.bind(this));
    }

    get selectedOptions() {
      return [...this.querySelectorAll("fieldset")].map(
        (set) => set.querySelector("input:checked")?.value
      );
    }

    onChange() {
      const options = this.selectedOptions;
      const variant = this.variants.find((v) =>
        v.options.every((opt, i) => opt === options[i])
      );
      this.updateLabels();
      this.updateState(variant);
    }

    updateLabels() {
      this.querySelectorAll("fieldset").forEach((set) => {
        const value = set.querySelector("input:checked")?.value;
        const label = set.querySelector("[data-selected-value]");
        if (label && value) label.textContent = value;
      });
    }

    updateState(variant) {
      const idInput = document.querySelector(`#ProductForm-${this.sectionId} [name="id"]`);
      const priceEl = document.getElementById(`Price-${this.sectionId}`);
      const stockEl = document.getElementById(`Stock-${this.sectionId}`);
      const buttons = [
        document.querySelector(`#ProductForm-${this.sectionId} [type="submit"]`),
        document.querySelector(`[type="submit"][form="ProductForm-${this.sectionId}"]`),
      ].filter(Boolean);
      const stickyPrice = document.getElementById(`StickyPrice-${this.sectionId}`);

      if (!variant) {
        buttons.forEach((b) => {
          b.disabled = true;
          const label = b.querySelector(".btn__label") || b;
          label.textContent = window.stillStrings.unavailable;
        });
        return;
      }

      if (idInput) {
        idInput.value = variant.id;
        idInput.dispatchEvent(new Event("change", { bubbles: true }));
      }

      // Price
      if (priceEl) {
        priceEl.innerHTML = this.priceHTML(variant);
      }
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);

      // Availability
      buttons.forEach((b) => {
        const label = b.querySelector(".btn__label") || b;
        b.disabled = !variant.available;
        label.textContent = variant.available
          ? window.stillStrings.addToCart
          : window.stillStrings.soldOut;
      });

      // Stock note
      if (stockEl) {
        const qty = variant.inventory_quantity;
        const threshold = parseInt(stockEl.dataset.threshold || "0", 10);
        const managed = stockEl.dataset.managed === "true";
        if (!variant.available) {
          stockEl.hidden = true;
        } else if (managed && threshold > 0 && qty > 0 && qty <= threshold) {
          stockEl.hidden = false;
          stockEl.classList.add("buybox__stock--low");
          stockEl.textContent = stockEl.dataset.lowText.replace("[count]", qty);
        } else {
          stockEl.hidden = false;
          stockEl.classList.remove("buybox__stock--low");
          stockEl.textContent = stockEl.dataset.inStockText;
        }
      }

      // Media
      if (variant.featured_media) {
        const slide = document.querySelector(
          `#Gallery-${this.sectionId} [data-media-id="${variant.featured_media.id}"]`
        );
        if (slide) slide.parentElement.scrollTo({ left: slide.offsetLeft, behavior: REDUCED_MOTION ? "auto" : "smooth" });
      }

      // URL
      window.history.replaceState({}, "", `${this.dataset.url}?variant=${variant.id}`);
    }

    priceHTML(variant) {
      const price = formatMoney(variant.price);
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        return `<span class="price"><span class="price__sale">${price}</span> <s class="price__compare">${formatMoney(variant.compare_at_price)}</s></span>`;
      }
      return `<span class="price">${price}</span>`;
    }
  }
  customElements.define("variant-picker", VariantPicker);

  /* ------------------------------------------------------------------------
     Product gallery — scroll snap + dots/thumbs + lightbox
     ------------------------------------------------------------------------ */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector(".gallery__track");
      this.slides = [...this.querySelectorAll(".gallery__slide")];
      this.dots = [...this.querySelectorAll(".gallery__dot")];
      this.thumbs = [...this.querySelectorAll(".gallery__thumb")];
      if (!this.track || this.slides.length < 1) return;

      if (this.slides.length > 1) {
        let last = 0;
        this.track.addEventListener(
          "scroll",
          () => {
            const now = Date.now();
            if (now - last < 90) return;
            last = now;
            const index = Math.round(this.track.scrollLeft / this.slides[0].offsetWidth);
            this.setCurrent(Math.min(this.slides.length - 1, Math.max(0, index)));
          },
          { passive: true }
        );

        this.dots.forEach((dot, i) =>
          dot.addEventListener("click", () => this.goTo(i))
        );
        this.thumbs.forEach((thumb, i) =>
          thumb.addEventListener("click", () => this.goTo(i))
        );
      }

      // Lightbox
      this.addEventListener("click", (e) => {
        const slideBtn = e.target.closest("[data-lightbox-src]");
        if (slideBtn) this.openLightbox(slideBtn.dataset.lightboxSrc, slideBtn.dataset.lightboxAlt);
      });
    }

    setCurrent(index) {
      this.dots.forEach((d, i) => d.setAttribute("aria-current", i === index ? "true" : "false"));
      this.thumbs.forEach((t, i) => t.setAttribute("aria-current", i === index ? "true" : "false"));
    }

    goTo(index) {
      const slide = this.slides[index];
      if (slide) {
        this.track.scrollTo({ left: slide.offsetLeft, behavior: REDUCED_MOTION ? "auto" : "smooth" });
      }
    }

    openLightbox(src, alt) {
      const dialog = document.createElement("dialog");
      dialog.className = "lightbox-dialog";
      dialog.innerHTML = `
        <div class="lightbox">
          <img src="${src}" alt="${alt || ""}">
          <button type="button" class="icon-btn lightbox__close" aria-label="${window.stillStrings.close || "Close"}">
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round"/></svg>
          </button>
        </div>`;
      document.body.appendChild(dialog);
      dialog.addEventListener("click", (e) => {
        if (e.target === dialog || e.target.closest(".lightbox__close") || e.target.classList.contains("lightbox")) {
          dialog.close();
        }
      });
      dialog.addEventListener("close", () => dialog.remove());
      dialog.showModal();
    }
  }
  customElements.define("product-gallery", ProductGallery);

  /* ------------------------------------------------------------------------
     Sticky ATC bar — appears once the real buy button scrolls above the fold.
     Time-throttled scroll listener with a trailing check: no dependence on
     rAF or IntersectionObserver, both of which can stall in webviews.
     ------------------------------------------------------------------------ */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.target = document.getElementById(this.dataset.watch);
      if (!this.target) return;
      let last = 0;
      const check = () => {
        this.classList.toggle("is-visible", this.target.getBoundingClientRect().bottom < 0);
      };
      this.onScroll = () => {
        clearTimeout(this.trailing);
        const now = Date.now();
        if (now - last >= 90) {
          last = now;
          check();
        } else {
          this.trailing = setTimeout(check, 120);
        }
      };
      window.addEventListener("scroll", this.onScroll, { passive: true });
      check();
    }

    disconnectedCallback() {
      window.removeEventListener("scroll", this.onScroll);
    }
  }
  customElements.define("sticky-atc", StickyAtc);

  /* ------------------------------------------------------------------------
     Accordion — smooth height animation
     ------------------------------------------------------------------------ */
  class DetailsAccordion extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector("details");
      this.summary = this.querySelector("summary");
      this.content = this.querySelector("[data-accordion-content]");
      if (!this.details || !this.summary || !this.content || REDUCED_MOTION) return;

      this.summary.addEventListener("click", (e) => {
        e.preventDefault();
        this.details.open ? this.collapse() : this.expand();
      });
    }

    expand() {
      clearTimeout(this.closeGuard);
      this.content.getAnimations().forEach((a) => a.cancel());
      this.details.open = true;
      const height = this.content.scrollHeight;
      this.content.animate(
        [{ height: "0px", opacity: 0 }, { height: `${height}px`, opacity: 1 }],
        { duration: 320, easing: "cubic-bezier(0.32, 0.72, 0, 1)" }
      );
    }

    collapse() {
      clearTimeout(this.closeGuard);
      this.content.getAnimations().forEach((a) => a.cancel());
      const height = this.content.scrollHeight;
      const done = () => (this.details.open = false);
      const anim = this.content.animate(
        [{ height: `${height}px`, opacity: 1 }, { height: "0px", opacity: 0 }],
        { duration: 260, easing: "cubic-bezier(0.32, 0.72, 0, 1)" }
      );
      anim.onfinish = done;
      // Guard: close even if the animation never finishes (throttled webviews).
      this.closeGuard = setTimeout(done, 340);
    }
  }
  customElements.define("details-accordion", DetailsAccordion);

  /* ------------------------------------------------------------------------
     Search overlay + predictive search
     ------------------------------------------------------------------------ */
  class SearchOverlay extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector(".search-overlay__input");
      this.results = this.querySelector("[data-search-results]");
      this.debounce = null;

      this.addEventListener("keydown", (e) => {
        if (e.key === "Escape") this.close();
        if (e.key === "Tab" && this.classList.contains("is-open")) trapFocus(this, e);
      });
      this.addEventListener("click", (e) => {
        if (e.target.matches("[data-search-close], [data-search-close] *")) this.close();
        if (e.target === this) this.close();
      });
      if (this.input) {
        this.input.addEventListener("input", () => {
          clearTimeout(this.debounce);
          this.debounce = setTimeout(() => this.fetchResults(), 220);
        });
      }
    }

    open(invoker) {
      this.invoker = invoker;
      this.classList.add("is-open");
      this.removeAttribute("inert");
      document.body.classList.add("is-locked");
      requestAnimationFrame(() => this.input && this.input.focus());
    }

    close() {
      this.classList.remove("is-open");
      this.setAttribute("inert", "");
      document.body.classList.remove("is-locked");
      if (this.invoker) this.invoker.focus({ preventScroll: true });
    }

    async fetchResults() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.results.innerHTML = "";
        return;
      }
      try {
        const root = window.Shopify?.routes?.root || "/";
        const res = await fetch(
          `${root}search/suggest?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[type]=product,page,article&resources[limit]=6`
        );
        const text = await res.text();
        const html = new DOMParser().parseFromString(text, "text/html");
        const fresh = html.querySelector("#PredictiveResults");
        if (fresh) this.results.innerHTML = fresh.innerHTML;
      } catch (e) {
        /* silent */
      }
    }
  }
  customElements.define("search-overlay", SearchOverlay);

  document.addEventListener("click", (e) => {
    const opener = e.target.closest("[data-search-open]");
    if (!opener) return;
    e.preventDefault();
    const overlay = document.getElementById("SearchOverlay");
    if (overlay) overlay.open(opener);
  });

  document.addEventListener("keydown", (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "k") {
      e.preventDefault();
      const overlay = document.getElementById("SearchOverlay");
      if (overlay) overlay.classList.contains("is-open") ? overlay.close() : overlay.open(document.body);
    }
  });

  /* ------------------------------------------------------------------------
     Free shipping meter
     ------------------------------------------------------------------------ */
  window.updateShippingMeter = (totalCents) => {
    document.querySelectorAll("[data-shipping-meter]").forEach((meter) => {
      const threshold = window.stillSettings.freeShippingThreshold;
      if (!threshold) return;
      const label = meter.querySelector(".shipping-meter__label");
      const fill = meter.querySelector(".shipping-meter__fill");
      const pct = Math.min(100, (totalCents / threshold) * 100);
      if (fill) {
        fill.style.width = `${pct}%`;
        fill.classList.toggle("is-unlocked", pct >= 100);
      }
      if (label) {
        label.classList.toggle("is-unlocked", pct >= 100);
        label.innerHTML =
          pct >= 100
            ? window.stillStrings.shippingFree
            : window.stillStrings.shippingAway.replace(
                "[amount]",
                `<strong>${formatMoney(threshold - totalCents)}</strong>`
              );
      }
    });
  };

  /* ------------------------------------------------------------------------
     Quick add (single-variant products)
     ------------------------------------------------------------------------ */
  document.addEventListener("click", async (e) => {
    const btn = e.target.closest("[data-quick-add]");
    if (!btn) return;
    e.preventDefault();
    btn.disabled = true;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || "/"}cart/add.js`, {
        ...fetchConfig("javascript"),
        headers: { "Content-Type": "application/json", Accept: "application/javascript" },
        body: JSON.stringify({
          items: [{ id: parseInt(btn.dataset.quickAdd, 10), quantity: 1 }],
          sections: "cart-drawer,cart-icon-bubble",
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (result.status) throw new Error(result.description);
      const drawer = document.getElementById("CartDrawer");
      if (drawer) {
        drawer.renderState({ sections: result.sections, item_count: null });
        drawer.open(btn);
      }
      announce(window.stillStrings.added);
    } catch (err) {
      window.location.href = btn.dataset.quickAddUrl || window.location.href;
    } finally {
      btn.disabled = false;
    }
  });
})();
